import * as React from 'react';
import { useStyles, useFormControlContext, useBoolean, useId, isUndefined, useMergedRef } from '@sravni/react-utils';
import { Icon } from '../../Icon/src/Icon.js';
import { Spinner } from '../../Spinner/src/Spinner.js';
import modules_56feba8c from '../styles/TextInput.module.less.js';

function fixControlledValue(value) {
    if (typeof value === 'undefined' || value === null) {
        return '';
    }
    return value;
}
/**
 * @description The Text Input component allows user to type in text
 *
 * @component
 * @example
 * ```jsx
 * <TextInput name="field" value="value" />
 * ```
 */
const TextInput = React.forwardRef((props, ref) => {
    const { id, autoFocus, value, label, placeholder, postfix, icon, leftIcon, disabled, invalid, readOnly, loading, positionInGroup, directionGroup, background, className, type, onFocus, onBlur, onChange, onClick, ...restProps } = props;
    const cx = useStyles(modules_56feba8c);
    const formControlContext = useFormControlContext();
    const containerRef = React.useRef(null);
    const inputSegmentRef = React.useRef(null);
    const inputRef = React.useRef();
    const [focused, setFocused] = useBoolean(false);
    const [filled, setFilled] = useBoolean(value !== undefined && value !== '');
    const inputId = useId(id ? '' : 'textInput', id);
    const isActive = focused && !readOnly;
    const isDisabled = formControlContext.disabled || disabled;
    const isInvalid = formControlContext.invalid || invalid;
    const hasIcon = icon || loading;
    const onlyPlaceholder = !!placeholder && !label;
    const classNames = cx(className, 'container', {
        focused,
        active: isActive,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        [`background-${background}`]: background,
        [`group-${directionGroup}`]: directionGroup,
        [`group-${positionInGroup}`]: positionInGroup,
    });
    const classNamesLabel = cx('label', {
        active: isActive,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'left-margin': !!leftIcon,
    });
    const classNamesPlaceholder = cx('placeholder', {
        active: isActive,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-label': label && focused,
        hidden: label && !focused,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'left-margin': !!leftIcon,
    });
    const classNamesInput = cx('input', {
        active: isActive,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        hidden: !readOnly && !focused,
    });
    const classNamesInputSegment = cx('input-segment', {
        centered: isUndefined(label) && (filled || focused),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'only-placeholder': onlyPlaceholder,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'left-margin': !!leftIcon,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
    });
    const iconElement = React.useMemo(() => {
        if (loading) {
            return React.createElement(Spinner, { className: cx('icon') });
        }
        if (icon) {
            return React.createElement(Icon, { className: cx('icon'), icon: icon });
        }
        return null;
    }, [cx, icon, loading]);
    const postfixElement = React.useMemo(() => {
        if (postfix && filled)
            return React.createElement("div", { className: cx('input-postfix') },
                "\u00A0",
                postfix);
        return null;
    }, [postfix, filled, cx]);
    const labelElement = React.useMemo(() => {
        if (label) {
            return (React.createElement("label", { htmlFor: inputId, className: classNamesLabel }, label));
        }
        return null;
    }, [inputId, label, classNamesLabel]);
    const placeholderElement = React.useMemo(() => {
        if ((label && readOnly) || filled) {
            return null;
        }
        if (placeholder) {
            return (React.createElement("label", { htmlFor: inputId, className: classNamesPlaceholder }, placeholder));
        }
        return null;
    }, [inputId, label, placeholder, filled, readOnly, classNamesPlaceholder]);
    const handleContainerClick = (event) => {
        if (disabled) {
            return;
        }
        inputRef.current?.focus();
        setFocused.on();
        // TODO: Might be worth redefining the onClick event to React.MouseEvent<HtmlDivElement>
        onClick?.(event);
    };
    const handleContainerBlur = (event) => {
        // needs to prevent extra onBlur callback executing on TextInput wrapper
        const relTarget = event.relatedTarget;
        if (containerRef.current?.contains(relTarget)) {
            event.stopPropagation();
        }
    };
    const handleContainerFocus = (event) => {
        // needs to prevent extra onFocus callback executing on TextInput wrapper
        const relTarget = event.relatedTarget;
        if (relTarget === inputSegmentRef.current || relTarget === containerRef.current) {
            event.stopPropagation();
        }
    };
    const handleFocus = React.useCallback((event) => {
        if (!focused) {
            setFocused.on();
            onFocus?.(event);
        }
    }, [focused, onFocus, setFocused]);
    const handleBlur = React.useCallback((event) => {
        const relTarget = event.relatedTarget;
        if (relTarget === inputSegmentRef.current || relTarget === containerRef.current)
            return;
        setFocused.off();
        onBlur?.(event);
    }, [onBlur, setFocused]);
    const handleChange = React.useCallback((event) => {
        onChange?.(event);
    }, [onChange]);
    React.useEffect(() => {
        if (value !== undefined && value !== '') {
            setFilled.on();
        }
        else {
            setFilled.off();
        }
    }, [setFilled, value]);
    return (React.createElement("div", { "data-qa": "TextInput", ref: containerRef, className: classNames, tabIndex: -1, onClick: handleContainerClick, onBlur: handleContainerBlur, onFocus: handleContainerFocus },
        leftIcon && React.createElement(Icon, { className: cx('icon', 'left'), icon: leftIcon }),
        React.createElement("div", { ref: inputSegmentRef, className: classNamesInputSegment, tabIndex: -1 },
            React.createElement("div", { className: cx('input-wrapper') },
                postfix && React.createElement("div", { className: cx('input-value') }, fixControlledValue(value)),
                React.createElement("input", { autoFocus: autoFocus, spellCheck: "false", autoComplete: "off", id: inputId, ref: useMergedRef(inputRef, ref), type: type, value: fixControlledValue(value), disabled: isDisabled, readOnly: readOnly, className: classNamesInput, onFocus: handleFocus, onBlur: handleBlur, onChange: handleChange, ...restProps }),
                postfixElement)),
        labelElement,
        placeholderElement,
        iconElement));
});
TextInput.defaultProps = {
    type: 'text',
};
// Для этого компонента при указании displayName не отображается автодокументация в сторибуке. Надо разобраться с магией
// TextInput.displayName = 'TextInput';

export { TextInput, fixControlledValue };
//# sourceMappingURL=TextInput.js.map
